import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { useLatest } from 'ahooks';
import { ThemeContext } from '@/pages/home/wrappers/common';
import Mask from '@home/components/Mask';
import {
  SYSTEM_THEME_DARK,
  SYSTEM_THEME_LIGHT,
} from '@/pages/home/_config/config';
import type {
  IEmbedPreference,
  IEmbedPreferenceIframeProps,
  IPreferenceUpdateMessage,
  TEmbedLanguage,
  TEmbedTheme,
} from './types';
import {
  buildEmbedPreferenceUrl,
  PREFERENCE_UPDATE_TYPE,
  readThemeFromStorage,
  resolveEmbedTargetOrigin,
} from './utils';
import './index.scss';

const normalizeLanguage = (locale: string): TEmbedLanguage =>
  locale === 'en-US' ? 'en-US' : 'zh-CN';

const normalizeTheme = (raw: string | undefined): TEmbedTheme =>
  raw === SYSTEM_THEME_DARK ? SYSTEM_THEME_DARK : SYSTEM_THEME_LIGHT;

/**
 * 情报模块 HTML 嵌入 iframe：同步主站 language/theme，协议对齐经分 SecureIframeRender fallback。
 * 不含 embed token 鉴权；AuthPath ?token= 由上层拼入 url。
 */
const EmbedPreferenceIframe: React.FC<IEmbedPreferenceIframeProps> = ({
  url,
  className,
  style,
  sandbox,
  useWatermark = false,
  onMessage,
}) => {
  const { locale } = useIntl();
  const themeContext = useContext(ThemeContext);
  const themeRaw = themeContext?.themeValue || readThemeFromStorage();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const expectedOriginRef = useRef<string>('');

  const [iframeUrl, setIframeUrl] = useState<string>('');

  const preference = useMemo<IEmbedPreference>(
    () => ({
      language: normalizeLanguage(locale),
      theme: normalizeTheme(themeRaw),
    }),
    [locale, themeRaw],
  );
  const preferenceRef = useRef(preference);
  preferenceRef.current = preference;
  const onMessageLatest = useLatest(onMessage);

  const postPreferenceToIframe = useCallback((pref: IEmbedPreference) => {
    const origin = expectedOriginRef.current;
    const win = iframeRef.current?.contentWindow;
    if (!origin || !win) return;

    const payload: IPreferenceUpdateMessage = {
      type: PREFERENCE_UPDATE_TYPE,
      language: pref.language,
      theme: pref.theme,
    };
    win.postMessage(payload, origin);
  }, []);

  const handleIframeLoad = useCallback(() => {
    postPreferenceToIframe(preferenceRef.current);
  }, [postPreferenceToIframe]);

  useEffect(() => {
    const trimmed = url?.trim() || '';
    if (!trimmed) {
      setIframeUrl('');
      expectedOriginRef.current = '';
      return;
    }
    expectedOriginRef.current = resolveEmbedTargetOrigin(trimmed);
    setIframeUrl(buildEmbedPreferenceUrl(trimmed, preferenceRef.current));
  }, [url]);

  useEffect(() => {
    if (!iframeUrl) return;
    postPreferenceToIframe(preference);
  }, [preference, iframeUrl, postPreferenceToIframe]);

  useEffect(() => {
    if (!iframeUrl) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      const contentWindow = iframeRef.current?.contentWindow;
      if (!contentWindow || event?.source !== contentWindow) {
        return;
      }

      const expectedOrigin = expectedOriginRef.current;
      if (!expectedOrigin || event?.origin !== expectedOrigin) {
        return;
      }

      onMessageLatest.current?.(event);
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [iframeUrl]);

  if (!url?.trim()) return null;

  const wrapClass = classNames(
    'dbi-embed-preference-iframe',
    'dtb-db-iframe-wrap',
    className,
  );

  return (
    <div className={wrapClass} style={style}>
      <iframe
        ref={iframeRef}
        key={url.trim()}
        className="dbi-embed-preference-iframe__iframe dtb-db-iframe"
        src={iframeUrl}
        sandbox={sandbox}
        title="dbi-embed-preference-iframe"
        onLoad={handleIframeLoad}
      />
      {useWatermark && (
        <div className="dbi-embed-preference-iframe__watermark">
          <Mask />
        </div>
      )}
    </div>
  );
};

export default EmbedPreferenceIframe;
